import React from 'react'
import { classNames } from '../../utils/classNames'

export interface AvatarProps extends React.HTMLAttributes<HTMLDivElement> {
  src?: string
  alt?: string
  name?: string
  size?: 'sm' | 'md' | 'lg' | 'xl'
  variant?: 'default' | 'rounded' | 'square'
  fallback?: React.ReactNode
}

export const Avatar = React.forwardRef<HTMLDivElement, AvatarProps>(
  (
    {
      src,
      alt,
      name,
      size = 'md',
      variant = 'default',
      fallback,
      className = '',
      ...props
    },
    ref
  ) => {
    const [imageError, setImageError] = React.useState(false)
    const [imageLoaded, setImageLoaded] = React.useState(false)

    const sizeClasses = {
      sm: 'avatar-sm',
      md: 'avatar-md',
      lg: 'avatar-lg',
      xl: 'avatar-xl',
    }

    const variantClasses = {
      default: 'rounded-full',
      rounded: 'rounded-lg',
      square: 'rounded-none',
    }

    const classes = classNames(
      'avatar',
      sizeClasses[size],
      variantClasses[variant],
      className
    )

    const getInitials = (name: string) => {
      if (!name || typeof name !== 'string') return '?'
      return name
        .split(/\s+/)
        .filter(Boolean)
        .map((word) => word.charAt(0))
        .join('')
        .toUpperCase()
        .slice(0, 2) || '?'
    }

    const getFallbackContent = (): React.ReactNode => {
      if (fallback !== undefined) {
        if (typeof fallback === 'string' && fallback.length > 3) {
          return getInitials(fallback)
        }
        return fallback
      }
      return name ? getInitials(name) : '?'
    }

    const handleImageError = () => {
      setImageError(true)
    }

    const handleImageLoad = () => {
      setImageLoaded(true)
    }

    const showImage = src && !imageError
    const showFallback = !showImage || !imageLoaded

    return (
      <div ref={ref} className={classes} {...props}>
        {showImage && (
          <img
            src={src}
            alt={alt || name || 'Avatar'}
            className={classNames(
              'w-full h-full object-cover',
              variantClasses[variant],
              !imageLoaded && 'hidden'
            )}
            onError={handleImageError}
            onLoad={handleImageLoad}
          />
        )}
        
        {showFallback && (
          <div className="w-full h-full flex items-center justify-center bg-neutral-200 dark:bg-navy-600 text-neutral-600 dark:text-neutral-300 text-sm font-medium overflow-hidden">
            {getFallbackContent()}
          </div>
        )}
      </div>
    )
  }
)

Avatar.displayName = 'Avatar'
